<template>
  <div
    class="bdt-cell"
    :class="[collectedClasses, { 'bdt-cell--clickable': hasClick }]"
    :style="collectedStyle"
    v-bind="collectedAttrs"
    @click="onClick"
    @contextmenu="onContextMenu"
  >
    <span v-if="renderMode === 'html'" v-html="html"></span>
    <span v-else-if="renderMode === 'node'" ref="nodeHost"></span>
    <!-- Wrapped rather than a bare text node so a resized (fixed-layout)
         column can clip it with an ellipsis; a bare node is an anonymous flex
         item and cannot be styled. -->
    <span v-else>{{ text }}</span>
  </div>
</template>

<script>
import { createCellFacade } from "./CellFacade";
import { getFieldValue } from "./fieldValue";

export default {
  name: "BreadDataTableCell",
  props: {
    row: { type: Object, required: true },
    header: { type: Object, required: true },
    tableData: { type: Array, default: () => [] },
  },
  data: () => ({
    renderMode: "text",
    html: "",
    text: "",
    collectedClasses: [],
    collectedStyle: {},
    collectedAttrs: {},
    mountedNode: null,
  }),
  computed: {
    def() {
      return this.header._def || {};
    },
    hasClick() {
      return Boolean(this.def.cellClick || this.def.clickMenu);
    },
  },
  watch: {
    // Row objects are mutated in place (flag toggles, inline updates), so a
    // deep watch is what keeps a legacy formatter's output reactive.
    row: { handler: "render", deep: true },
    header: "render",
  },
  created() {
    this.render();
  },
  mounted() {
    this.attachNode();
  },
  updated() {
    this.attachNode();
  },
  beforeDestroy() {
    this.destroyNode();
  },
  methods: {
    render() {
      const { cell, collected } = createCellFacade({
        row: this.row,
        column: this.header,
        tableData: this.tableData,
      });
      this._cell = cell;

      const field = this.header.field;
      const raw = getFieldValue(this.row, field);
      const formatter = this.def.formatter;

      let result = raw;
      // Only output that a model author deliberately produced may be treated as
      // markup. Raw column values are ALWAYS text — see the render branches below.
      let formatterProduced = false;

      if (typeof formatter === "function") {
        formatterProduced = true;
        try {
          result = formatter(cell, this.def.formatterParams || {});
        } catch (e) {
          // A throwing formatter must not take the whole table down.
          // eslint-disable-next-line no-console
          console.error(
            `[BreadDataTable] formatter failed for column "${this.header.value}"`,
            e
          );
          result = "";
        }
      } else if (formatter === "datetime") {
        result = raw ? this.$moment(new Date(raw)).format("YYYY-MM-DD") : "";
      }

      // Harvest whatever the formatter poked onto the fake element.
      this.collectedClasses = collected.classes.slice();
      this.collectedStyle = Object.assign({}, collected.style);
      this.collectedAttrs = Object.assign({}, collected.attrs);

      this.destroyNode();

      if (result && typeof result === "object" && result.nodeType) {
        this.renderMode = "node";
        this.pendingNode = result;
      } else if (formatterProduced && typeof result === "string") {
        // A function formatter in a model file is developer-authored code, so its
        // return value is markup by design (Tabulator ran these with
        // htmlOutput: true). Note this inherits Tabulator's existing exposure:
        // those formatters interpolate DB values into HTML strings without
        // escaping. Not a regression, but it is real — the durable fix is
        // migrating formatters to slot-rendered components, at which point this
        // branch can be deleted entirely.
        this.renderMode = "html";
        this.html = result;
      } else {
        // Raw column values, and anything else, are escaped text. Never sniff
        // markup out of a database value.
        this.renderMode = "text";
        this.text = result === null || result === undefined ? "" : String(result);
      }
    },
    attachNode() {
      if (this.renderMode !== "node" || !this.pendingNode) return;
      const host = this.$refs.nodeHost;
      if (!host || host.firstChild === this.pendingNode) return;
      host.innerHTML = "";
      host.appendChild(this.pendingNode);
      this.mountedNode = this.pendingNode;
      this.pendingNode = null;
    },
    destroyNode() {
      // Legacy Vue.extend formatters mount a real component per cell and never
      // tear it down. Destroy it here so the table doesn't leak at page size 500.
      if (this.mountedNode && this.mountedNode.__vue__) {
        try {
          this.mountedNode.__vue__.$destroy();
        } catch (e) {
          /* noop */
        }
      }
      this.mountedNode = null;
    },
    onClick(e) {
      if (this.def.clickMenu) {
        const items = this.def.clickMenu(e, this._cell) || [];
        this.$emit("open-menu", { event: e, items, cell: this._cell });
        return;
      }
      if (this.def.cellClick) this.def.cellClick(e, this._cell);
    },
    onContextMenu(e) {
      const menu = this.def.contextMenu;
      if (!menu) return;
      e.preventDefault();
      const items = typeof menu === "function" ? menu(e, this._cell) : menu;
      this.$emit("open-menu", { event: e, items: items || [], cell: this._cell });
    },
  },
};
</script>

<style scoped>
/* The <td> has zero padding (see table-tokens.css) and this element supplies it
   instead, so a formatter-applied tint like .tbl-cell--warning covers the full
   cell and its left bar lands on the column edge. */
.bdt-cell {
  display: flex;
  align-items: center;
  min-height: var(--tbl-row-height, 46px);
  width: 100%;
  /* Vertical padding so a cell holding stacked stage-alert pills breathes
     instead of clamping them together. */
  padding: 7px 16px;
}
.bdt-cell--clickable {
  cursor: pointer;
}
</style>
